import { useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useForm,
  type DefaultValues,
  type Path,
  type PathValue,
  type Resolver,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodSchema } from "zod";
import { OPTIMISTIC_LOCK_HEADER, isApiErrorCode } from "@ckgo2/shared";
import { api, ApiError, type ApiResponse } from "@/api/client";
import { humanizeFieldMessage } from "@/lib/errorMessages";
import i18n from "@/i18n";
import { useSelectValueSync } from "./useSelectValueSync";
import { autoSlugFor } from "./editPageRules";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseEditPageOptions<TForm extends Record<string, any>, TData> {
  /** Query key for single item, e.g. "activity" */
  resourceKey: string;
  /** Query key for list invalidation, e.g. "activities" */
  listKey: string;
  /** API base path of the item (detail = `${apiPath}/${id}`), e.g. "/api/admin/inspirations" */
  apiPath: string;
  /** Zod schema for form validation (optional — omit for no validation) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema?: ZodSchema<any>;
  /** Default form values */
  defaultValues: TForm;
  /**
   * Base admin path of the entity, e.g. "/inspirations"; the detail lives at `${navigateTo}/${id}`.
   * After save the editor never leaves for the list: a new item replaces `/new` with its detail.
   */
  navigateTo: string;
  /** Field to watch for auto-slug generation (default: "title") */
  slugSourceField?: keyof TForm & string;
  /** `false` = hook slug negeneruje (entita bez slugu, např. recenze); výchozí `true` */
  generateSlug?: boolean;
  /**
   * `true` = formulář se resetuje po každém načtení detailu, i když data zůstala stejná (routa,
   * jejíž `PUT` nemění `updatedAt`); výchozí `false`
   */
  resetOnRefetch?: boolean;
  /** Transform loaded data before resetting the form */
  transformOnLoad?: (data: TData) => TForm;
  /** Transform form data before sending to API */
  transformOnSave?: (data: TForm) => unknown;
  /** Extra query dependencies for reset effect (e.g., wait for dropdown data) */
  resetDeps?: unknown[];
}

/**
 * `zodResolver`, který kódy z registru (vize F13.9 — katalogová schémata hlásí `slug_format`
 * místo textu) přeloží přes `humanizeFieldMessage`. Ostatní zprávy nechává, jak jsou — mimo
 * katalog se nic nemění. Bez toho by inline chyba pole ukázala holý kód.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function codeTranslatingResolver(schema: ZodSchema<any>): Resolver<any> {
  const base = zodResolver(schema);
  return async (values, context, options) => {
    const result = await base(values, context, options);
    translateCodedMessages(result.errors);
    return result;
  };
}

/** Projde strom chyb react-hook-form a kódy z registru nahradí textem slovníku (na místě). */
function translateCodedMessages(node: unknown): void {
  if (!node || typeof node !== "object") return;
  const entry = node as Record<string, unknown>;
  if (typeof entry.message === "string" && isApiErrorCode(entry.message)) {
    entry.message = humanizeFieldMessage(entry.message);
  }
  for (const [key, value] of Object.entries(entry)) {
    if (key !== "ref" && key !== "message") translateCodedMessages(value);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useEditPage<TForm extends Record<string, any>, TData = TForm>(
  options: UseEditPageOptions<TForm, TData>,
) {
  const {
    resourceKey,
    listKey,
    apiPath,
    schema,
    defaultValues,
    navigateTo,
    slugSourceField = "title",
    generateSlug = true,
    resetOnRefetch = false,
    transformOnLoad,
    transformOnSave,
    resetDeps = [],
  } = options;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  // Optimistic-lock state (Block 6.1 Řez 1b): the `updatedAt` we loaded into the form (sent
  // back so the server can detect a concurrent save), and a one-shot flag set when the user
  // chose to overwrite a conflicting change.
  const baseVersionRef = useRef<string | undefined>(undefined);
  const forceOverwriteRef = useRef(false);

  const form = useForm<TForm>({
    ...(schema ? { resolver: codeTranslatingResolver(schema) } : {}),
    defaultValues: defaultValues as DefaultValues<TForm>,
  });

  const { reset, watch, setValue } = form;
  // Read at render: the `formState` proxy only subscribes to keys read here, and the reset effect
  // below needs the current value without re-running whenever the user types.
  const isDirtyRef = useRef(false);
  isDirtyRef.current = form.formState.isDirty;
  // The `id` the form was last reset for; `undefined` until the first data, so the first data for
  // any `id` always resets (even over a form a child component dirtied on mount).
  const lastResetIdRef = useRef<string | undefined>(undefined);

  // Fetch item if editing
  const {
    data: itemData,
    dataUpdatedAt,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: [resourceKey, id],
    queryFn: () => api.get<ApiResponse<TData>>(`${apiPath}/${id}`),
    enabled: !isNew,
  });

  // Reset form when data loads. Data for a different `id` than the last reset always resets. For
  // the same `id` (refetch after a save, a dropdown list arriving via `resetDeps`) the reset is
  // skipped over a dirty form, so edits typed after "Uloženo" survive; the optimistic-lock base
  // then stays the one from our own save, and a foreign save in between surfaces as a 409.
  // With `resetOnRefetch` every fetch is a new dependency: a refetch returning equal data keeps
  // the `itemData` object (structural sharing) yet must still replace the just-saved values with
  // the server's. Without it the key is a constant 0, so other editors reset exactly as before.
  const refetchKey = resetOnRefetch ? dataUpdatedAt : 0;
  useEffect(() => {
    if (!itemData?.data) return;
    if (lastResetIdRef.current === id && isDirtyRef.current) return;
    const formData = transformOnLoad
      ? transformOnLoad(itemData.data)
      : (itemData.data as unknown as TForm);
    reset(formData as DefaultValues<TForm>);
    lastResetIdRef.current = id;
    // Capture the version we just loaded — a later save echoes it as the optimistic-lock base.
    baseVersionRef.current = (itemData.data as { updatedAt?: string }).updatedAt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, itemData, reset, refetchKey, ...resetDeps]);

  // Číselníky pro `<select>`y dojíždějí vlastními dotazy, typicky až PO resetu výše — bez tohoto
  // dorovnání prohlížeč uloženou volbu zahodí (`<option>` ještě neexistuje) a ukáže první položku.
  // Schválně až ZA reset efektem, aby v rámci jednoho commitu viděl už načtené hodnoty.
  useSelectValueSync(form);

  // Auto-generate slug from source field. `watch` is not a React hook, so the conditional call is
  // fine; without `generateSlug` the form does not re-render on every keystroke in the source.
  const sourceValue = generateSlug ? watch(slugSourceField as Path<TForm>) : undefined;
  useEffect(() => {
    const slug = autoSlugFor({ generateSlug, isNew, sourceValue });
    if (slug !== null) {
      setValue("slug" as Path<TForm>, slug as PathValue<TForm, Path<TForm>>, {
        shouldDirty: false,
      });
    }
  }, [sourceValue, isNew, setValue, generateSlug]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: TForm) => {
      const apiData = transformOnSave ? transformOnSave(data) : data;
      if (isNew) {
        return api.post<ApiResponse<TData>>(apiPath, apiData);
      }
      // Echo the loaded version so the server can reject a stale overwrite (409). When the user
      // chose "overwrite" after a conflict, drop the header to force it. The flag is cleared in
      // onSuccess, not here — so if a forced save fails (transient 5xx/network) the user's
      // overwrite choice survives the retry instead of bouncing back to a 409.
      const headers =
        baseVersionRef.current && !forceOverwriteRef.current
          ? { [OPTIMISTIC_LOCK_HEADER]: baseVersionRef.current }
          : undefined;
      return api.put<ApiResponse<TData>>(`${apiPath}/${id}`, apiData, headers);
    },
    onError: (error: Error) => {
      // Set server-side validation errors on form fields so they show inline
      if (error instanceof ApiError && error.fieldErrors) {
        for (const [field, messages] of Object.entries(error.fieldErrors)) {
          if (messages.length > 0) {
            form.setError(field as Path<TForm>, {
              type: "server",
              message: humanizeFieldMessage(messages[0]),
            });
          }
        }
      }
    },
    // Async on purpose: TanStack Query awaits `onSuccess` before resolving `mutateAsync`, so
    // `isSubmitting` (and the disabled Save button) lasts until a new item's detail is loaded —
    // a second click cannot POST a duplicate.
    onSuccess: async (response, variables) => {
      // The save landed, so a pending "force overwrite" choice has been honoured — clear it.
      forceOverwriteRef.current = false;
      // Keep the optimistic-lock base in sync with our OWN write: the PUT returns the row with its
      // freshly-bumped updatedAt, so a rapid second save doesn't fire a spurious self-conflict
      // 409 while the background refetch is still in flight.
      const responseData = (response as ApiResponse<TData & { id?: string; updatedAt?: string }>)
        .data;
      if (responseData?.updatedAt) baseVersionRef.current = responseData.updatedAt;
      // Prefix match: also refreshes the shared slim lists (`useAdminLists`) keyed under it.
      queryClient.invalidateQueries({ queryKey: [listKey] });
      if (isNew) {
        const newId = responseData?.id;
        if (!newId) return;
        // POST returns the bare table row, GET /:id the enriched detail (relations such as a
        // destination's `inspirationIds`). Seed the detail cache with the GET shape BEFORE
        // switching the URL, so the editor shows full data without a spinner and the next save
        // does not wipe relations. The POST response is never written into the detail cache.
        try {
          await queryClient.fetchQuery({
            queryKey: [resourceKey, newId],
            queryFn: () => api.get<ApiResponse<TData>>(`${apiPath}/${newId}`),
            staleTime: 0,
          });
        } catch {
          // The item is saved either way; the detail then loads normally (with a spinner) and its
          // error, if it persists, shows through the query's own error state.
        }
        navigate(`${navigateTo}/${newId}${location.search}`, { replace: true });
        return;
      }
      queryClient.invalidateQueries({ queryKey: [resourceKey, id] });
      reset(variables as DefaultValues<TForm>);
    },
  });

  const onSubmit = async (data: TForm) => {
    try {
      await saveMutation.mutateAsync(data);
    } catch (err) {
      // Concurrent-edit conflict (Block 6.1 Řez 1b): another editor saved since this form
      // loaded. Offer to overwrite (force) or keep editing; on overwrite, retry once without
      // the version header so the server skips the check.
      if (err instanceof ApiError && err.isConflict) {
        // `err.message` = text kódu `conflict` ze slovníku i s časem změny (`params.when`);
        // přeložil ho klient v `apiErrorFromBody`, jediné překladové místo (vize F13.9).
        const overwrite = window.confirm(
          `${err.message}\n\n${i18n.t("shared.editPage.conflictChoice")}`,
        );
        if (overwrite) {
          forceOverwriteRef.current = true;
          await saveMutation.mutateAsync(data);
        }
        return;
      }
      throw err;
    }
  };

  return {
    id,
    isNew,
    isLoading: !isNew && isLoading,
    isError: !isNew && isError,
    error,
    form,
    saveMutation,
    onSubmit,
    navigate,
    queryClient,
    itemData: itemData?.data ?? null,
  };
}
